import { ALL_NAMESPACE_EVENTS, BUILD_EVENT, TSchema } from '@core';
import * as Events from '@core/events';
import { EEVents } from '@core/constants';
import { TFlowType } from '@core/types';

//Field handlers
import * as change from '@core/handlers/field/change';
import * as mount from '@core/handlers/field/mount';
import * as blur from '@core/handlers/field/blur';
import * as focus from '@core/handlers/field/focus';
import * as templating from './common/templating';
import * as api from '@core/handlers/field/api';
import * as data from '@core/handlers/field/data';
import * as clearFields from '@core/handlers/field/clearFields';
import * as visibilityConditions from '@core/handlers/field/visibilityConditions';
import * as htmlEventParser from '@core/handlers/field/htmlEventParser';
import * as formatters from '@core/handlers/field/formatters';
import * as filter from '@core/handlers/field/filter';
import * as masks from '@core/handlers/field/masks';
import * as validations from '@core/handlers/field/validations';
import * as keyup from '@core/handlers/field/keyup';
import * as keydown from '@core/handlers/field/keydown';

//Form handlers
import * as hooks from '@core/handlers/form/hooks';
import * as steps from '@core/handlers/form/steps';
import * as validate from '@core/handlers/form/validate';
import * as bindFields from '@core/handlers/form/bindFields';
import * as formLevelVisibilityConditions from '@core/handlers/form/visibilityConditions';
import * as formTemplating from '@core/handlers/form/templating';

const register = (
  observer: Events.Observer,
  flows: any,
  component?: any | TSchema,
) => {
  const flow = flows() as TFlowType;
  Object.keys(flow).forEach((event: EEVents) => {
    flow[event].forEach((handler) => {
      if (handler.events) {
        const events = handler.events(component);
        if (Array.isArray(events) && !events.includes(event)) return;
      }
      observer.subscribe(BUILD_EVENT(event, component?.name), handler.handler);
    });
  });
};

const fieldFlows = () => ({
  [EEVents.ON_FIELD_MOUNT]: [
    templating,
    mount,
    api,
    validations,
    formatters,
    visibilityConditions,
    clearFields,
    masks,
    data,
  ],
  [EEVents.ON_FIELD_CHANGE]: [
    htmlEventParser,
    filter,
    change,
    validations,
    formatters,
    visibilityConditions,
    clearFields,
    api,
    data,
    masks,
  ],
  [EEVents.ON_FIELD_KEYUP]: [
    htmlEventParser,
    filter,
    keyup,
    validations,
    formatters,
    visibilityConditions,
    clearFields,
    api,
    data,
    masks,
  ],
  [EEVents.ON_FIELD_KEYDOWN]: [
    htmlEventParser,
    filter,
    keydown,
    validations,
    formatters,
    visibilityConditions,
    clearFields,
    api,
    data,
    masks,
  ],
  [EEVents.ON_FIELD_REHYDRATE]: [validations],
  [EEVents.ON_FIELD_CLEARED]: [
    formatters,
    masks,
    filter,
    validations,
    visibilityConditions,
    api,
    data,
  ],
  [EEVents.ON_FIELD_BINDED]: [
    data,
    formatters,
    masks,
    filter,
    validations,
    api,
    visibilityConditions,
    clearFields,
  ],
  [EEVents.ON_FIELD_CLICK]: [
    validations,
    api,
    visibilityConditions,
    clearFields,
  ],
  [EEVents.ON_FIELD_BLUR]: [
    blur,
    formatters,
    masks,
    validations,
    api,
    visibilityConditions,
    clearFields,
    data,
  ],
  [EEVents.ON_FIELD_FOCUS]: [focus, masks],
  [EEVents.AFTER_FIELD_API_CALL]: [
    htmlEventParser,
    filter,
    validations,
    formatters,
    masks,
    data,
    visibilityConditions,
    clearFields,
  ],
});

const formFlows = () => ({
  [EEVents.ON_SCOPE_CHANGE]: [formTemplating, formLevelVisibilityConditions],
  [EEVents.ON_FORM_MOUNT]: [
    templating,
    hooks,
    steps,
    formLevelVisibilityConditions,
  ],
  [EEVents.VALIDATE_FORM]: [validate],
  [EEVents.ON_FORM_SUBMIT]: [hooks],
  [EEVents.ON_FORM_UN_MOUNT]: [hooks],
  [EEVents.NAVIGATE_STEP]: [steps],
  [EEVents.NAVIGATE_STEP_BACK]: [steps],
  [EEVents.NAVIGATE_STEP_FORWARD]: [steps],
  [ALL_NAMESPACE_EVENTS(EEVents.ON_SCOPE_CHANGE)]: [
    formLevelVisibilityConditions,
    templating,
  ],
  [EEVents.ON_FORM_REHYDRATE]: [formLevelVisibilityConditions],
  [EEVents.ON_FIELD_CHANGE]: [formLevelVisibilityConditions],
  [EEVents.BIND_FIELDS]: [bindFields],
  [EEVents.ON_FORM_DESTROY_FIELD]: [data],
});

export { register, fieldFlows, formFlows };
